<script lang="tsx" setup>
import HomeLetterRow from "@/components/Home/HomeLetterRow.vue"
import HomeAboutOverlay from "@/components/Home/HomeAboutOverlay.vue"
import { ref, onMounted, reactive, watch, computed, nextTick } from "vue"
import { useRouter } from "vue-router"
import { useMotion } from "@vueuse/motion"
import { useJackpot } from "@/composables/use-jackpot"
import { useSpring } from "@/composables/use-spring"
import { menuOpen } from "@/composables/use-menu-machine"
import { useIntro } from "@/composables/use-intro"
import { useProjects, waitForProjects } from "@/store/projects"
import { fetchThumbnailsForUris } from "@/queries/home-thumbnails"
import { clamp } from "@/utils/math"
import { cleanHTML } from "@/utils/clean"

const INITIAL_BATCH = 5
const BATCH_SIZE = 10
const MAX_IMAGES = 60

const urls = ref<string[]>([])
const thumbSrcsets = reactive<Record<string, string>>({})
const thumbUris = reactive<Record<string, string>>({})

const {
  state,
  poolReady,
  cImage,
  mImage,
  readyCount,
  chargeLevel,
  pressedLetter,
  hoveredLetter,
  split,
  pause,
  resume,
  onPressStart,
  onPressEnd,
  onHoverStart,
  onHoverEnd,
  onMouseMove,
} = useJackpot(urls)

const { showIntro, dismissIntro } = useIntro()

const cGrow = computed(() => clamp(split.value, 0.04, 0.96))
const mGrow = computed(() => 1 - cGrow.value)

watch(menuOpen, isOpen => {
  if (isOpen) pause()
  else resume()
})

const { visibleStudents } = useProjects()
let shuffledUris: string[] = []
let cursor = 0
let loading = false

function fisherYates<T>(arr: T[]): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

async function loadBatch(count: number) {
  if (loading) return
  if (!shuffledUris.length || cursor >= shuffledUris.length) return
  if (urls.value.length >= MAX_IMAGES) return
  loading = true
  const batch = shuffledUris.slice(cursor, cursor + count)
  cursor += count
  const items = await fetchThumbnailsForUris(batch)
  items.forEach(item => {
    thumbSrcsets[item.thumbUrl] = item.srcset
    thumbUris[item.thumbUrl] = item.uri
  })
  urls.value = [...urls.value, ...items.map(i => i.thumbUrl)]
  loading = false
}

async function initPool() {
  await waitForProjects()
  const seen = new Set<string>()
  for (const s of visibleStudents.value) {
    if (s.hasThumbnail && !seen.has(s.uri)) seen.add(s.uri)
  }
  shuffledUris = fisherYates([...seen])
  await loadBatch(INITIAL_BATCH)
}

watch(readyCount, count => {
  if (count >= urls.value.length && urls.value.length < MAX_IMAGES) {
    loadBatch(BATCH_SIZE)
  }
})

const aboutTextShort = `
<p>
  A collection of objects, of people, of ideas; a movement with sufficient momentum or fissile material to start a chain reaction. This is what, and where, we are in June 2026.
  </p>
`

onMounted(() => {
  initPool()
})

function handlePressStart(letter: "c" | "m") {
  onPressStart(letter)
}

function handlePressEnd() {
  onPressEnd()
  // Navigation is intentionally left commented while we workshop the spin:
  // if (!currentStudent.value) return
  // router.push("/" + currentStudent.value.uri)
}

const currentStudent = computed(() => {
  const uri = thumbUris[cImage.value] || thumbUris[mImage.value]
  if (!uri) return null
  return visibleStudents.value.find(s => s.uri === uri) ?? null
})

const infoOpen = computed(
  () =>
    (state.value === "result" || state.value === "spinning") &&
    !!currentStudent.value,
)

// Spring-driven grow/shrink for the student info, replacing a hard v-if so the
// block eases open and closed (height owned by the spring, like the menu pill).
const infoRef = ref<HTMLElement>()
const infoContentRef = ref<HTMLElement>()
const { config } = useSpring("snappy")
const { apply } = useMotion(infoRef, {
  initial: { height: 0, opacity: 0 },
})

watch(
  infoOpen,
  async open => {
    await nextTick()
    apply({
      height: open ? (infoContentRef.value?.scrollHeight ?? 0) : 0,
      opacity: open ? 1 : 0,
      transition: { height: config, opacity: config },
    })
  },
  { flush: "post" },
)
</script>

<template>
  <div
    class="absolute inset-0 w-full h-full flex flex-col lg:gap-6 lg:p-6 gap-2 p-2"
  >
    <HomeLetterRow
      letter="c"
      :image="cImage"
      :srcset="thumbSrcsets[cImage]"
      :state="state"
      :charge-level="chargeLevel"
      :pressed-letter="pressedLetter"
      :hovered-letter="hoveredLetter"
      :grow="cGrow"
      @press-start="handlePressStart('c')"
      @press-end="handlePressEnd()"
      @hover-start="onHoverStart('c')"
      @hover-end="onHoverEnd('c')"
      @mousemove="(e: MouseEvent) => onMouseMove('c', e.clientX, e.clientY)"
    />

    <!-- Center slot: student info (spring-animated height) -->
    <div class="lg:-my-3 -my-1">
      <div ref="infoRef" class="shrink-0">
        <div
          ref="infoContentRef"
          class="lg:text-[calc(1vw+1em)] leading-[1.15] mx-auto text-center"
          :class="{ 'pointer-events-none': state === 'spinning' }"
        >
          <router-link
            v-if="currentStudent"
            :to="`/${currentStudent.uri}`"
            class="transition-colors flex flex-col items-center justify-center gap-1 w-full border py-3 px-3 leading-none hover:text-gold"
          >
            <div
              class="line-clamp-1 w-full text-center"
              v-html="cleanHTML(currentStudent.name)"
            ></div>
            <div
              class="italic line-clamp-1 w-full text-center"
              v-html="cleanHTML(currentStudent.title)"
            ></div>
          </router-link>
        </div>
      </div>
    </div>

    <HomeLetterRow
      letter="m"
      :image="mImage"
      :srcset="thumbSrcsets[mImage]"
      :state="state"
      :charge-level="chargeLevel"
      :pressed-letter="pressedLetter"
      :hovered-letter="hoveredLetter"
      :grow="mGrow"
      @press-start="handlePressStart('m')"
      @press-end="handlePressEnd()"
      @hover-start="onHoverStart('m')"
      @hover-end="onHoverEnd('m')"
      @mousemove="(e: MouseEvent) => onMouseMove('m', e.clientX, e.clientY)"
    />

    <HomeAboutOverlay
      v-if="showIntro"
      :pool-ready="poolReady"
      :about-html="aboutTextShort"
      @dismiss="dismissIntro()"
    />
  </div>
</template>
